export const isFreeLikePlan = (plan) => {
    if (!plan) return false;
    return plan.planType === 'FREE' || (plan.price ?? 0) <= 0;
};

export const isPaidProPlan = (plan) => (
    plan?.planType === 'PAID' && (plan.price ?? 0) > 0
);

// Named tiers are identified by their stable backend `name`, not by "any paid plan" - once
// several paid tiers exist, "the first paid plan" is ambiguous. Used by upsell modals that need
// to link to a specific tier (e.g. Market Data/AI assistant upsells target Pro specifically).
export const findPlanByName = (plans = [], name) => (
    (plans || []).find((plan) => plan?.name === name) || null
);

// The cheapest paid plan that includes a feature - the upsell target for that feature, so the
// offer follows the backend's entitlements instead of a hard-coded tier name.
export const findCheapestPlanWithFeature = (plans = [], feature) => (
    (plans || [])
        .filter((plan) => isPaidProPlan(plan) && (plan.features || []).includes(feature))
        .sort((a, b) => (a.price ?? 0) - (b.price ?? 0))[0] || null
);

export const getVisibleSubscriptionPlans = (plans = [], { hideFreePlan = false } = {}) => {
    const visiblePlans = [];
    let hasFreePlan = false;

    plans.forEach((plan) => {
        if (isFreeLikePlan(plan)) {
            if (!hideFreePlan && !hasFreePlan) {
                visiblePlans.push(plan);
                hasFreePlan = true;
            }
            return;
        }

        visiblePlans.push(plan);
    });

    return visiblePlans;
};

// Months per billing cadence - drives the per-month equivalent shown on pricing cards.
export const BILLING_INTERVAL_MONTHS = { YEARLY: 12, SEMIANNUAL: 6, MONTHLY: 1 };

export const toMonthlyEquivalent = (amount, interval) => {
    const months = BILLING_INTERVAL_MONTHS[interval] || 12;
    return Math.round((Number(amount) / months) * 100) / 100;
};

// Price fields for one cadence of a plan, regardless of whether that cadence is still sold to
// new customers - a monthly subscriber stays a monthly subscriber after monthly stops being
// offered. Null if the plan has no price for that cadence. YEARLY (and anything unknown) means
// the plan's primary `price` slot.
export const getPlanPricingForInterval = (plan, interval) => {
    if (!plan) return null;
    switch (interval) {
        case 'SEMIANNUAL':
            return plan.semiannualBillingPrice != null
                ? { interval, price: plan.semiannualBillingPrice, seatPrice: plan.additionalUserSemiannualPrice ?? null }
                : null;
        case 'MONTHLY':
            return plan.monthlyBillingPrice != null
                ? { interval, price: plan.monthlyBillingPrice, seatPrice: plan.additionalUserMonthlyPrice ?? null }
                : null;
        default:
            return { interval: 'YEARLY', price: plan.price ?? null, seatPrice: plan.additionalUserPrice ?? null };
    }
};

// The cadences a plan can be bought on, in display order. The primary/annual option always
// exists; the 6-month and monthly alternatives only when the plan carries a price for them AND
// the backend hasn't switched them off for new checkouts (SubscriptionPlanDto.*BillingOffered -
// absent on older payloads, hence the `!== false`).
export const getPlanBillingOptions = (plan) => {
    if (!plan) return [];
    const options = [getPlanPricingForInterval(plan, 'YEARLY')];
    if (plan.semiannualBillingOffered !== false) {
        const semiannual = getPlanPricingForInterval(plan, 'SEMIANNUAL');
        if (semiannual) options.push(semiannual);
    }
    if (plan.monthlyBillingOffered !== false) {
        const monthly = getPlanPricingForInterval(plan, 'MONTHLY');
        if (monthly) options.push(monthly);
    }
    return options;
};

export const getPlanDisplay = (plan, t) => {
    const billingInterval = plan?.billingInterval || 'NONE';
    const price = typeof plan?.price === 'number' ? plan.price : null;

    let monthlyPrice = price;
    let yearlyPrice = price;
    if (price != null) {
        if (billingInterval === 'YEARLY') {
            monthlyPrice = Math.round((price / 12) * 100) / 100;
        } else if (billingInterval === 'SEMIANNUAL') {
            monthlyPrice = Math.round((price / 6) * 100) / 100;
            yearlyPrice = Math.round(price * 2 * 100) / 100;
        } else if (billingInterval === 'MONTHLY') {
            yearlyPrice = Math.round(price * 12 * 100) / 100;
        }
    }

    const formatPriceLabel = (value) => (typeof value === 'number' ? value.toFixed(2) : value);

    return {
        name: plan?.name || t?.('pages.settings.billing.free', 'Free') || 'Free',
        planType: plan?.planType || 'FREE',
        price,
        monthlyPrice,
        yearlyPrice,
        monthlyPriceLabel: price == null
            ? t?.('pages.settings.billing.custom', 'Custom')
            : formatPriceLabel(monthlyPrice),
        yearlyPriceLabel: price == null
            ? t?.('pages.settings.billing.custom', 'Custom')
            : formatPriceLabel(yearlyPrice),
        maxUsers: plan?.maxUsers ?? null,
        currency: plan?.currency || 'EUR',
        billingInterval,
    };
};

export const getCurrencySymbol = (currency) => {
    if (!currency) return '€';
    const normalized = String(currency).toUpperCase();
    return normalized === 'EUR' ? '€' : normalized;
};
